/**
 * SuccessCelebration Component
 *
 * Animated success celebration with confetti-like effect.
 * Used for post approval, creation, and other positive actions.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Confetti particle component
function Particle({ index, color }) {
  const randomX = Math.random() * 200 - 100;
  const randomY = Math.random() * -200 - 50;
  const randomRotation = Math.random() * 720 - 360;
  const duration = 0.8 + Math.random() * 0.4;

  return (
    <motion.div
      initial={{
        x: 0,
        y: 0,
        scale: 1,
        rotate: 0,
        opacity: 1,
      }}
      animate={{
        x: randomX,
        y: randomY,
        scale: 0,
        rotate: randomRotation,
        opacity: 0,
      }}
      transition={{
        duration,
        ease: "easeOut",
        delay: index * 0.02,
      }}
      style={{
        position: "absolute",
        width: 8,
        height: 8,
        borderRadius: index % 2 === 0 ? "50%" : "2px",
        backgroundColor: color,
      }}
    />
  );
}

Particle.propTypes = {
  index: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
};

// Success check animation
const checkVariants = {
  hidden: {
    scale: 0,
    opacity: 0,
  },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 15,
      delay: 0.1,
    },
  },
};

const circleVariants = {
  hidden: {
    scale: 0,
    opacity: 0,
  },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 250,
      damping: 20,
    },
  },
};

const textVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.3,
      duration: 0.4,
    },
  },
};

const confettiColors = [
  "#4CAF50", // Green
  "#1A73E8", // Blue
  "#FB8C00", // Orange
  "#9C27B0", // Purple
  "#00BCD4", // Cyan
  "#FF5722", // Deep Orange
];

function SuccessCelebration({
  show = false,
  title = "Success!",
  message = "Your action was completed successfully.",
  onDismiss,
  autoHide = true,
  autoHideDelay = 3000,
}) {
  const [showConfetti, setShowConfetti] = useState(false);
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (show) {
      setShowConfetti(true);
      // Generate particles
      const newParticles = Array.from({ length: 30 }, (_, i) => ({
        id: i,
        color: confettiColors[i % confettiColors.length],
      }));
      setParticles(newParticles);

      // Auto-hide
      if (autoHide && onDismiss) {
        const timer = setTimeout(() => {
          onDismiss();
        }, autoHideDelay);
        return () => clearTimeout(timer);
      }
    } else {
      setShowConfetti(false);
      setParticles([]);
    }
  }, [show, autoHide, autoHideDelay, onDismiss]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            zIndex: 2000,
            backdropFilter: "blur(8px)",
          }}
          onClick={onDismiss}
        >
          <MDBox textAlign="center" p={4} sx={{ maxWidth: 400 }}>
            {/* Animated Circle with Check */}
            <MDBox
              position="relative"
              display="flex"
              justifyContent="center"
              alignItems="center"
              mb={3}
            >
              {/* Confetti Particles */}
              {showConfetti && (
                <MDBox
                  position="absolute"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  {particles.map((particle) => (
                    <Particle key={particle.id} index={particle.id} color={particle.color} />
                  ))}
                </MDBox>
              )}

              {/* Success Circle */}
              <motion.div variants={circleVariants} initial="hidden" animate="visible">
                <MDBox
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    backgroundColor: "rgba(76, 175, 80, 0.1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "3px solid #4CAF50",
                  }}
                >
                  <motion.div variants={checkVariants} initial="hidden" animate="visible">
                    <Icon
                      sx={{
                        fontSize: "48px !important",
                        color: "#4CAF50",
                      }}
                    >
                      check
                    </Icon>
                  </motion.div>
                </MDBox>
              </motion.div>
            </MDBox>

            {/* Title */}
            <motion.div variants={textVariants} initial="hidden" animate="visible">
              <MDTypography variant="h3" fontWeight="bold" mb={1}>
                {title}
              </MDTypography>
            </motion.div>

            {/* Message */}
            <motion.div
              variants={textVariants}
              initial="hidden"
              animate="visible"
              transition={{ delay: 0.4 }}
            >
              <MDTypography variant="body1" color="text" mb={3} sx={{ opacity: 0.8 }}>
                {message}
              </MDTypography>
            </motion.div>

            {/* Dismiss Button */}
            {onDismiss && (
              <motion.div
                variants={textVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: 0.5 }}
              >
                <MDButton
                  variant="gradient"
                  color="success"
                  onClick={onDismiss}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                  }}
                >
                  Continue
                </MDButton>
              </motion.div>
            )}
          </MDBox>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

SuccessCelebration.propTypes = {
  show: PropTypes.bool,
  title: PropTypes.string,
  message: PropTypes.string,
  onDismiss: PropTypes.func,
  autoHide: PropTypes.bool,
  autoHideDelay: PropTypes.number,
};

export default SuccessCelebration;
